import type { EntityATI, ExperimentLayer, SearchJiraProductType } from '../../../common/types';

import { getAtlasPartials } from './atlas';
import { getCompassPartials } from './compass';
import { getConfluencePartials } from './confluence';
import { getJiraPartials } from './jira';
import { type ResultsSort } from './results-sort';
import { getGraphPartials } from './third-party-graph-entity';
import { getGooglePartials, getMicrosoftPartials, getSlackPartials } from './third-party-legacy';
import { getTrelloPartials } from './trello';

export interface CommonFilters {
	contributorsFilter?: string[];
	range?: {
		lastModified?: {
			gt?: string;
			lt?: string;
		};
	};
}

export type SearchConfluenceRangeField = 'LASTMODIFIED' | 'CREATED';

export interface ConfluenceRangeFilter {
	field: SearchConfluenceRangeField;
	gt?: string;
	lt?: string;
}

export interface ConfluenceFilters {
	ancestorIdsFilter: string[];
	contributorsFilter: string[];
	isVerified?: boolean;
	labelsFilter: string[];
	owners: string[];
	range: ConfluenceRangeFilter[];
	spacesFilter: string[];
	titleMatchOnly?: boolean;
}

export interface JiraFilters {
	issueFilter: {
		assigneeARIs: string[];
		projectARIs: string[];
	};
	projectFilter?: {
		projectTypes?: SearchJiraProductType[];
	};
}

export type SearchThirdPartyRangeField = 'LASTMODIFIED' | 'CREATED';

export interface ThirdPartyRangeFilter {
	field: SearchThirdPartyRangeField;
	gt?: string;
	lt?: string;
}

export interface ThirdPartyFilters {
	integrations?: string[];
	range?: ThirdPartyRangeFilter[];
	subtypes: string[];
	titleMatchOnly: boolean;
}

export type SearchPageQueryVariables = {
	after?: string;
	cloudIdARI: string;
	commonFilters: CommonFilters;
	confluenceFilters: ConfluenceFilters;
	disableQueryReplacement?: boolean;
	entities: EntityATI[]; // ATI of the types needed
	experimentId?: string;
	experimentLayers?: ExperimentLayer[];
	jiraFilters: JiraFilters;
	limit: number;
	query: string;
	queryVersion: number;
	searchSessionId: string;
	shadowExperimentId?: string;
	sort: ResultsSort[];
	sourceProduct?: string;
	tenantId: string;
	thirdPartyFilters: ThirdPartyFilters;
};
export type ResultsCountQueryVariables = {
	after?: string;
	cloudIdARI: string;
	commonFilters: CommonFilters;
	confluenceFilters: ConfluenceFilters;
	disableQueryReplacement?: boolean;
	entities: EntityATI[]; // ATI of the types needed
	jiraFilters: JiraFilters;
	limit: number;
	query: string;
	queryVersion: number;
	searchSessionId: string;
	sort: any[];
	sourceProduct?: string;
	thirdPartyFilters: ThirdPartyFilters;
};

/**
 * These attributes are the base search result attributes and are requested for every entity.
 */
export const SEARCH_RESULTS_ATTRIBUTES = `
description
iconUrl
id
title
type
url
__typename
`;

export const getSupportedPartials = () => {
	return `
		${getConfluencePartials()}
		${getJiraPartials()}
		${getTrelloPartials()}
		${getAtlasPartials()}
		${getCompassPartials()}
		${getGooglePartials()}
		${getMicrosoftPartials()}
		${getSlackPartials()}
		${getGraphPartials()}
	`;
};

export const operationName = 'SearchPageQuery';

export const queryGenerator = (): string => {
	const partials = getSupportedPartials();

	const graphQuery = `
	query ${operationName}(
		$query: String!
		$cloudIdARI: String!
		$tenantId: ID!
		$limit: Int!
		$entities: [String!]!
		$queryVersion: Int
		$searchSessionId: String
		$commonFilters: SearchCommonFilter
		$confluenceFilters: SearchConfluenceFilter
		$jiraFilters: SearchJiraFilter
		$thirdPartyFilters:  SearchThirdPartyFilter
		$sort: [SearchSortInput]
		$sourceProduct: String
		$experimentId: String
		$shadowExperimentId: String
		$experimentLayers: [SearchExperimentLayer]
		$after: String
		$disableQueryReplacement: Boolean
	) {
		search {
			results: search(
				experience: "search-page"
				query: $query
				first: $limit
				after: $after
				sort: $sort
				disableQueryReplacement: $disableQueryReplacement
				filters: {
					entities: $entities
					locations: [$cloudIdARI]
					commonFilters: $commonFilters
					confluenceFilters: $confluenceFilters
					jiraFilters: $jiraFilters
					thirdPartyFilters: $thirdPartyFilters
				}
		analytics: {
					queryVersion: $queryVersion
					searchSessionId: $searchSessionId
					sourceProduct: $sourceProduct
				}
		experimentContext: {
			experimentId: $experimentId
			shadowExperimentId: $shadowExperimentId
			experimentLayers: $experimentLayers
		}
			) {
				edges {
					node {
						${SEARCH_RESULTS_ATTRIBUTES}
						${partials}
					}
				}
				queryInfo {
					originalQuery
					replacementQuery
					originalQueryType
					confidenceScore
					shouldTriggerAutocorrectionExperience
				}
				abTest {
					abTestId
					controlId
					experimentId
				}
				totalCount
				totalCounts {
					product
					count
				}
        pageInfo {
          endCursor
        }
			}
		}
	}
	`;
	return graphQuery;
};

export const resultsCountOperationName = 'ResultsCountQuery';

export const resultsCountQueryGenerator = (): string => {
	const graphQuery = `
	query ${resultsCountOperationName}(
		$query: String!
		$cloudIdARI: String!
		$limit: Int!
		$entities: [String!]!
		$queryVersion: Int
		$searchSessionId: String
		$commonFilters: SearchCommonFilter
		$confluenceFilters: SearchConfluenceFilter
		$jiraFilters: SearchJiraFilter
		$thirdPartyFilters:  SearchThirdPartyFilter
		$sort: [SearchSortInput]
		$sourceProduct: String
		$experimentId: String
		$shadowExperimentId: String
		$experimentLayers: [SearchExperimentLayer]
		$disableQueryReplacement: Boolean
	) {
		search {
			results: search(
				experience: "search-page"
				query: $query
				first: $limit
				sort: $sort
				disableQueryReplacement: $disableQueryReplacement
				filters: {
					entities: $entities
					locations: [$cloudIdARI]
					commonFilters: $commonFilters
					confluenceFilters: $confluenceFilters
					jiraFilters: $jiraFilters
					thirdPartyFilters: $thirdPartyFilters
				}
		analytics: {
					queryVersion: $queryVersion
					searchSessionId: $searchSessionId
					sourceProduct: $sourceProduct
				}
		experimentContext: {
			experimentId: $experimentId
			shadowExperimentId: $shadowExperimentId
			experimentLayers: $experimentLayers
		}
			) {

				queryInfo {
					originalQuery
					replacementQuery
					originalQueryType
					confidenceScore
					shouldTriggerAutocorrectionExperience
				}
				abTest {
					abTestId
					controlId
					experimentId
				}
				totalCount
				totalCounts {
					product
					count
				}
			}
		}
	}
	`;
	return graphQuery;
};
